// app.config.ts

import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  isDevMode,
  LOCALE_ID,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter, Router, withPreloading } from '@angular/router';
import { SelectivePreloadStrategy } from '@stock-core/selective-preload.strategy';
import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';

import { routes } from './app.stock.routes';

import { environment } from '@environments';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import '@progress/kendo-angular-intl/locales/nl-BE/all';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

import * as Sentry from '@sentry/angular';

import localeNlBe from '@angular/common/locales/nl-BE';

import { registerLocaleData } from '@angular/common';

import { firstValueFrom } from 'rxjs';

import {
  provideKendoLocalization,
  provideSharedInterceptors,
  SHARED_ENVIRONMENT_CONFIG,
  SIGNED_OUT_CONFIG,
  SignedOutConfig,
  TranslationLoaderService,
} from 'shared';
import { authHttpInterceptorFn, provideAuth0 } from '@auth0/auth0-angular';
import { provideServiceWorker, SwUpdate } from '@angular/service-worker';

// ✅ Register Dutch locale
registerLocaleData(localeNlBe, 'nl-BE');

// ✅ Translate loader factory
export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

// ✅ Async initializer
export async function appInitializerFactory() {
  const translate = inject(TranslateService);
  const loaderService = inject(TranslationLoaderService);

  try {
    const results = await Promise.allSettled([
      firstValueFrom(translate.use('nl')),
    ]);
    results.forEach((result, index) => {
      if (result.status === 'rejected') {
        console.error(`Initializer Task ${index + 1} failed:`, result.reason);
      }
    });
  } catch (err) {
    console.error('App initialization error:', err);
  } finally {
    loaderService.setLoading(false);
  }
}
const isSentryEnabled = !isDevMode() && location.hostname !== 'localhost';

/** Reports the service worker's update lifecycle to Sentry, to see whether devices pick up new deploys. */
function trackApplicationUpdates() {
  const swUpdate = inject(SwUpdate);
  if (!swUpdate.isEnabled) return;

  swUpdate.versionUpdates.subscribe((event) => {
    switch (event.type) {
      case 'VERSION_DETECTED':
        Sentry.addBreadcrumb({ category: 'app-update', message: `New version detected: ${event.version.hash}` });
        break;
      case 'VERSION_READY':
        Sentry.addBreadcrumb({
          category: 'app-update',
          message: `Version ready: ${event.currentVersion.hash} -> ${event.latestVersion.hash}`,
        });
        break;
      case 'VERSION_INSTALLATION_FAILED':
        Sentry.captureMessage(`Application update failed to install: ${event.error}`, 'warning');
        break;
    }
  });
  swUpdate.unrecoverable.subscribe((event) =>
    Sentry.captureMessage(`Service worker is in an unrecoverable state: ${event.reason}`, 'warning'),
  );
}

export const appConfig: ApplicationConfig = {
  providers: [
    ...(
      environment.auth0 ? [provideAuth0({
        domain: environment.auth0.domain,
        clientId: environment.auth0.clientId,
        authorizationParams: environment.auth0.authorizationParams,
        useRefreshTokens: environment.auth0.useRefreshTokens,
        cacheLocation: 'localstorage',
        httpInterceptor: environment.auth0.httpInterceptor,
        // A failed login callback ends on the signed-out page instead of looping back into the login
        errorPath: '/signed-out?reason=error',
      })] : []
    ),
    provideHttpClient(withInterceptors([
      // Custom HTTP interceptors from shared lib
      ...provideSharedInterceptors(),
      authHttpInterceptorFn
    ])),

    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: createTranslateLoader,
          deps: [HttpClient],
        },
      })
    ),

    provideAnimationsAsync(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withPreloading(SelectivePreloadStrategy)),

    // Localization
    provideKendoLocalization(),

    // Global env injection
    { provide: SHARED_ENVIRONMENT_CONFIG, useValue: environment },

    // Signed-out page
    {
      provide: SIGNED_OUT_CONFIG,
      useValue: {
        appName: 'CrownStock',
        startPath: '/home',
        lastApp: 'stock',
        lastAppCookieDomain: environment.lastAppCookieDomain,
        lastAppCookieName: environment.lastAppCookieName,
      } satisfies SignedOutConfig,
    },

    // Async translation loader
    provideAppInitializer(appInitializerFactory),

    // Sentry error reporting
    ...(isSentryEnabled ? [
      { provide: ErrorHandler, useValue: Sentry.createErrorHandler() },
      { provide: Sentry.TraceService, deps: [Router] },
      provideAppInitializer(trackApplicationUpdates),
    ] : []),
    { provide: LOCALE_ID, useValue: 'nl-BE' },
    provideServiceWorker('ngsw-worker.js', {
      enabled: location.hostname !== 'localhost',
      registrationStrategy: 'registerImmediately',
    }),
  ],
};
